import React, { useCallback, useEffect, useState } from 'react';
import { AuthContext } from './AuthContextCore';
import pb from '../services/pocketbase';
import { sendPublicSignup } from '../services/publicSignup';

const clearMarketingSessionMirrors = async () => {
  try {
    const { default: pbSync } = await import('../services/pbSync');
    pbSync.clearEncryption();
  } catch (_) {
    // Public shell must stay usable even if the heavier sync bundle fails to load.
  }
};

const initializeAuthenticatedSession = async ({ passwordSeed, email, authData, rememberMe = false }) => {
  const [
    { default: pbSync },
    { clearLicenseData },
    { resetAIUsageCache },
    { ensureProfileForCurrentUser },
    db,
  ] = await Promise.all([
    import('../services/pbSync'),
    import('../services/licensing'),
    import('../services/aiUsage'),
    import('../services/profileService'),
    import('../db'),
  ]);

  const canonicalEmail = (authData?.record?.email || email || '').toString().trim();
  await pbSync.initEncryptionForAuthenticatedSignIn(passwordSeed, canonicalEmail, {
    rememberDevice: rememberMe,
    legacySaltSeeds: [email, canonicalEmail, canonicalEmail.toLowerCase()],
  });

  await clearLicenseData();
  resetAIUsageCache();

  try {
    await ensureProfileForCurrentUser();
    await pb.send('/api/self/ensure-explorer-license', { method: 'POST' });
    await db.syncLocalTripleChecksToPocketBase().catch(() => {});
    await db.syncLocalJournalsToPocketBase().catch(() => {});
  } catch (error) {
    console.warn('[PublicAuth] Profile/license safety net failed:', error?.message || error);
  }
};

export const PublicAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setUser(pb.authStore.model);
    setSession(pb.authStore.isValid ? pb.authStore.token : null);
    setIsLoading(false);

    return pb.authStore.onChange((token, model) => {
      setUser(model);
      setSession(token);
    });
  }, []);

  const signUp = useCallback(async ({ email, password, name, captchaToken }) => {
    try {
      pb.authStore.clear();
      await clearMarketingSessionMirrors();
      setUser(null);
      setSession(null);

      const createdUser = await sendPublicSignup({ email, password, name, captchaToken });

      try {
        await pb.collection('users').requestVerification(email);
      } catch (error) {
        console.warn('[PublicAuth] Verification email could not be queued:', error?.message || error);
      }

      pb.authStore.clear();
      await clearMarketingSessionMirrors();
      return { data: { user: createdUser }, error: null };
    } catch (error) {
      pb.authStore.clear();
      await clearMarketingSessionMirrors();
      setUser(null);
      setSession(null);
      return { data: null, error: error?.message || 'Signup failed.' };
    }
  }, []);

  const signIn = useCallback(async ({ email, password, rememberMe = false }) => {
    try {
      const authData = await pb.collection('users').authWithPassword(email, password);
      await initializeAuthenticatedSession({ passwordSeed: password, email, authData, rememberMe });
      return { data: authData, error: null };
    } catch (error) {
      pb.authStore.clear();
      await clearMarketingSessionMirrors();
      return { data: null, error: error?.message || 'Login failed.' };
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    try {
      const authData = await pb.collection('users').authWithOAuth2({ provider: 'google' });
      const userEmail = authData?.record?.email || '';
      const userId = authData?.record?.id || '';
      await initializeAuthenticatedSession({
        passwordSeed: userId,
        email: userEmail,
        authData,
        rememberMe: true,
      });
      return { data: authData, error: null };
    } catch (error) {
      pb.authStore.clear();
      await clearMarketingSessionMirrors();
      return { data: null, error: error?.message || 'Google sign in failed.' };
    }
  }, []);

  const signOut = useCallback(async () => {
    pb.authStore.clear();
    await clearMarketingSessionMirrors();
    setUser(null);
    setSession(null);
    try {
      const [{ clearLicenseData }, { resetAIUsageCache }] = await Promise.all([
        import('../services/licensing'),
        import('../services/aiUsage'),
      ]);
      await clearLicenseData();
      resetAIUsageCache();
    } catch (_) {}
    return { error: null };
  }, []);

  const resetPassword = useCallback(async (email) => {
    try {
      await pb.collection('users').requestPasswordReset(email);
      return { error: null };
    } catch (error) {
      return { error: error?.message || 'Password reset failed.' };
    }
  }, []);

  const updatePassword = useCallback(async (newPassword) => {
    try {
      await pb.collection('users').update(pb.authStore.model.id, {
        password: newPassword,
        passwordConfirm: newPassword,
      });
      return { error: null };
    } catch (error) {
      return { error: error?.message || 'Password update failed.' };
    }
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      if (!pb.authStore.isValid) {
        return { data: null, error: 'No valid session' };
      }
      await pb.collection('users').authRefresh();
      return { data: { session: pb.authStore.token, user: pb.authStore.model }, error: null };
    } catch (error) {
      return { data: null, error: error?.message || 'Refresh failed.' };
    }
  }, []);

  const resendVerificationEmail = useCallback(async () => {
    try {
      const email = user?.email || pb.authStore.model?.email;
      if (!email) throw new Error('No user email found');
      await pb.collection('users').requestVerification(email);
      return { error: null };
    } catch (error) {
      return { error: error?.message || 'Verification email failed.' };
    }
  }, [user]);

  const value = {
    user,
    session,
    isAuthenticated: Boolean(session && user),
    isLoading,
    emailVerified: user?.verified ?? false,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
    refreshSession,
    resendVerificationEmail,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default PublicAuthProvider;
